/**
 * @file Mixin that handles connections between posts.
 */
import { mapState, mapGetters, mapActions } from 'vuex'
import { $ } from 'bits/jquery'
import { jsPlumb } from 'jsplumb'

export default {
  data() {
    return {
      $_connectorsMixin_existingConnections: [],
    }
  },
  computed: {
    ...mapState('postConnection', {
      $_connectorsMixin_postToDisconnectFromId: 'postToDisconnectFromId',
    }),
    ...mapGetters('postConnection', {
      $_connectorsMixin_connections: 'connections',
    }),
    ...mapGetters({
      $_connectorsMixin_format: 'format',
    }),
  },
  created() {
    this.$_connectorsMixin_lazyUpdateConnections = this.debounce(this.$_connectorsMixin_updateConnections, 125)
  },
  mounted() {
    this.$_connectorsMixin_existingConnections = []
    if (this.$_connectorsMixin_format === 'free') {
      jsPlumb && jsPlumb.setContainer(this.$el)
      this.$_connectorsMixin_makeConnections()
      this.$_connectorsMixin_registerDomChangeListenersForConnections()
    }
  },
  updated() {
    this.$_connectorsMixin_onUpdate()
  },
  watch: {
    $_connectorsMixin_postToDisconnectFromId() {
      this.$_connectorsMixin_onUpdate()
    },
    $_connectorsMixin_connections() {
      this.$_connectorsMixin_onUpdate()
    },
  },
  beforeDestroy() {
    this.$_connectorsMixin_destroyConnections()
  },

  methods: {
    ...mapActions('postConnection', ['completeDisconnection']),
    debounce(func, wait, immediate) {
      let timeout
      return () => {
        let context = this
        let args = arguments
        let later = () => {
          timeout = null
          if (!immediate) func.apply(context, args)
        }
        let callNow = immediate && !timeout
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
        if (callNow) func.apply(context, args)
      }
    },
    $_connectorsMixin_onUpdate() {
      if (this.$_connectorsMixin_format === 'free') {
        this.$_connectorsMixin_makeConnections()
      } else {
        this.$_connectorsMixin_destroyConnections()
      }
    },
    $_connectorsMixin_makeConnections() {
      // this is done in defer because we want this has to run after all the posts have been updated
      this.$nextTick(() => {
        if (this.$_connectorsMixin_format === 'free') {
          // remove connections no longer needed
          const removedConnections = []
          const keptConnections = []
          this.$_connectorsMixin_existingConnections.forEach(ec => {
            const isKept = this.$_connectorsMixin_connections.find(c => c.id === ec.cxn.id)
            const destination = isKept ? keptConnections : removedConnections
            destination.push(ec)
          })

          if (removedConnections.length > 0) {
            for (let i = 0, len = removedConnections.length; i < len; i++) {
              let connection = removedConnections[i]
              connection.jsp.removeOverlay('disconnect')
              jsPlumb && jsPlumb.deleteConnection(connection.jsp)
            }
            this.$_connectorsMixin_existingConnections = keptConnections
          }

          this.$_connectorsMixin_updateConnections()

          // go through connections list. update the ones that need updating.
          // add the ones that are not rendered yet.
          this.$_connectorsMixin_connections.forEach(cxn => {
            const existingConnection = this.$_connectorsMixin_existingConnections.find(ec => {
              return ec.cxn.id === cxn.id
            })
            // if this connection already exists
            if (!!existingConnection) {
              // is this connection a target for disconnection
              if (
                !!this.$_connectorsMixin_postToDisconnectFromId &&
                (this.$_connectorsMixin_postToDisconnectFromId === cxn.from_wish_id ||
                  this.$_connectorsMixin_postToDisconnectFromId === cxn.to_wish_id)
              ) {
                if (!existingConnection.disconnectOverlay) {
                  let disconnectOverlay = component => {
                    return $(
                      '<div><button class="floating-action-button-connector wish-disconnect-button"><i class="immaterial-icons">close</i></button></div>',
                    ).on('click', () => {
                      this.completeDisconnection({ connectionId: cxn.id })
                    })
                  }
                  existingConnection.disconnectOverlay = [
                    'Custom',
                    {
                      location: 0.3,
                      id: 'disconnect',
                      create: disconnectOverlay,
                    },
                  ]
                  existingConnection.jsp.addOverlay(existingConnection.disconnectOverlay)
                }
              } else {
                if (existingConnection.disconnectOverlay) {
                  existingConnection.jsp.removeOverlay('disconnect')
                  delete existingConnection.disconnectOverlay
                }
              }
              // otherwise draw new connection
            } else {
              let jsp = this.$_connectorsMixin_drawConnection(cxn.from_wish_id, cxn.to_wish_id, cxn.label)
              if (jsp != null) {
                this.$_connectorsMixin_existingConnections = this.$_connectorsMixin_existingConnections.concat([
                  {
                    cxn: cxn,
                    jsp: jsp,
                  },
                ])
              }
            }
          })
        }
      })
    },
    $_connectorsMixin_drawConnection(fromWishId, toWishId, label) {
      let overlays = []
      let fromElement = document.getElementById('wish-' + fromWishId)
      let toElement = document.getElementById('wish-' + toWishId)
      if (fromElement != null && toElement != null) {
        overlays = [
          [
            'PlainArrow',
            {
              location: 1,
              width: 12,
              length: 12,
            },
          ],
        ]
        if (label) {
          overlays.push([
            'Label',
            {
              label: label,
              id: 'label',
              cssClass: 'wish-connection-label',
            },
          ])
        }
        return (
          jsPlumb &&
          jsPlumb.connect({
            source: fromElement,
            target: toElement,
            endpoint: 'Blank',
            connector: [
              'Bezier',
              {
                curviness: 50,
              },
            ],
            anchor: 'AutoDefault',
            overlays: overlays,
          })
        )
      }
    },
    $_connectorsMixin_updateConnections() {
      // repaint all existing connections
      jsPlumb && jsPlumb.repaintEverything()
    },
    $_connectorsMixin_destroyConnections() {
      if (typeof jsPlumb !== 'undefined' && jsPlumb !== null) {
        jsPlumb && jsPlumb.deleteEveryConnection()
      }
      $(this.$el).off('.connectors')
      $(this.$el).off('.connectors', 'textarea,img')
    },
    $_connectorsMixin_registerDomChangeListenersForConnections() {
      $(this.$el).on('input.connectors', 'textarea', () => {
        this.$_connectorsMixin_lazyUpdateConnections()
      })
      $(this.$el).on('beethoven-load.connectors', 'img', () => {
        this.$_connectorsMixin_lazyUpdateConnections()
      })
    },
  },
}
